// @flow

import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import {
  ColumnsContainer,
  SectionComponent,
  Loader,
  View,
  Text,
  Button,
  ConfirmationModal,
  SchemaForm,
} from "@components";
import {
  EditAccessLevel,
  EditAccountDetailslModal,
  ConfirmResetPasswordModal,
  ConfirmImpersonateModal,
  AccountDetails,
  SetMissingEmailModal,
} from "@modules/employee/components";
import { connect } from "react-redux";
import * as employeeSelectors from "@modules/employee/selectors";
import { isLoading } from "@modules/account/selectors";
import employeeActions from "@modules/employee/actionCreator";
import accountActionCreators from "@modules/account/actionCreator";
import type { EmployeeType } from "@modules/employee/types";
import { formatDateTime } from "@transformers/date";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type {
  UserAccount,
  ImpersonateModal,
} from "@modules/employee/reducer/user-account-details/types";
import type { PropertiesSchema } from "@types";
import { formatAuthoredBy } from "@transformers/names";
import moment from "moment";
import * as loginSelectors from "@modules/account/selectors";
import { ChangePasswordModal } from "@modules/profile/components";
import profileActionCreators from "@modules/profile/actionCreator";
import * as profileSelectors from "@modules/profile/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import serviceActions from "@modules/service/actionCreator";
import type {
  DataSecurityGeneralSettings,
  PasswordPolicyType,
} from "@modules/service/reducer/system-access/types";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  employee: EmployeeType,
}>;

type PropsFromState = $Exact<{
  isEditAccountDetailsModalVisible: boolean,
  isConfirmResetPasswordModalVisible: boolean,
  impersonateModal: ImpersonateModal,
  userAccount: ?UserAccount,
  accessLevelSchema: ?PropertiesSchema,
  accessLeveValue: ?Object,
  isLoading: boolean,
  isEmployeeAccountLocked: boolean,
  isAccountActive: boolean,
  userType?: string,
  isChangePasswordModalVisible: boolean,
  dataSecurityGeneralSettings: DataSecurityGeneralSettings,
  passwordPolicy: PasswordPolicyType,
}>;

type PropsFromDispatch = $Exact<{
  toggleAccountDetailsModal: typeof employeeActions.ToggleEditAccountDetailsModal,
  toggleConfirmResetPasswordModal: typeof employeeActions.ToggleConfirmResetPasswordModal,
  toggleConfirmImpersonateModal: typeof employeeActions.ToggleUserAccountImpersonateModal,
  blockUserAccount: typeof employeeActions.BlockUserAccount,
  unblockUserAccount: typeof employeeActions.UnblockUserAccount,
  resetUserAccountPassword: typeof employeeActions.ResetUserAccountPassword,
  getUserAccount: typeof employeeActions.GetUserAccount,
  updateUserAccount: typeof employeeActions.UpdateUserAccount,
  getAccessLevelSchema: typeof employeeActions.GetAccessLevelSchema,
  updateAccessLevel: typeof employeeActions.UpdateAccessLevel,
  impersonate: typeof accountActionCreators.Impersonate,
  changePassword: typeof profileActionCreators.ChangePassword,
  toggleChangePasswordModal: typeof profileActionCreators.ToggleChangePasswordModal,
  resetOtp: typeof employeeActions.ResetUserOtp,
  getDataSecurityGeneralSettings: typeof serviceActions.GetDataSecurityGeneralSettings,
  unlockUserAccount: typeof employeeActions.UnlockUserAccount,
  getPasswordPolicy: typeof employeeActions.GetPasswordPolicy,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromPermissions,
}>;

type OwnState = {
  isEmailModalOpen: boolean,
};

const validate = {
  minNCharacters: function(value, count) {
    return value.length >= count;
  },
  lowerCase: function(value, count) {
    return (value.match(/[a-z]/g) || []).length >= count;
  },
  upperCase: function(value, count) {
    return (value.match(/[A-Z]/g) || []).length >= count;
  },
  gsDigitsOrSpecialChars: function(value, count) {
    return (value.match(/[\d!@#$%^&;()_+}{':;?/.<>=]/g) || []).length >= count;
  },
};

export function UserAccountDetailsGeneralInfo(props: Props) {
  const { isAccountActive, t, passwordPolicy } = props;

  const showResetPasswordBtn =
    ["ADMIN", "SERVICE_MANAGER", "CLIENT_ADMIN"].indexOf(props.userType) !== -1;

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const [passwordCriteria, setPasswordCriteria] = useState(generateCriteria());

  useEffect(() => {
    setPasswordCriteria(generateCriteria());
  }, [passwordPolicy]);

  const [isValidPassowrd, setIsValidPassowrd] = useState(false);
  const [changePasswordFormData, setChangePasswordFormData] = useState(null);

  const accountDetails = (
    <AccountDetails
      userAccount={props.userAccount}
      blockAccount={blockUserAccount}
      unblockAccount={unblockUserAccount}
      unlockTempBlockedAccount={handleUnlockUserAccount}
      onToggleSetEmailModal={handleToggleSetEmailModal}
    />
  );
  const passwordButtons = (
    <View alignItems="end" gap={3} padding="1rem 0 0 0">
      {[
        {
          text: props.t("RESET"),
          materialIcon: "lock_reset",
          onClick: showResetPasswordModal,
          hidden: !isAccountActive,
        },
        {
          text: props.t("IMPERSONATE"),
          color: "black",
          materialIcon: "social_distance",
          onClick: handleShowImpersonateModal,
          hidden:
            !props.hasPermission(PermissionEnum.PRIVILEGE_EMPLOYEE_IMPERSONATE) || !isAccountActive,
        },
        {
          text: props.t("CHANGE_PASSWORD"),
          color: "black",
          materialIcon: "password",
          onClick: toggleChangePasswordModal,
          hidden: !showResetPasswordBtn,
        },
        {
          text: props.t("RESET_OTP"),
          color: "black",
          materialIcon: "restart_alt",
          onClick: handleResetOtp,
          // show the reset otp button when account is active and multifactorial authentication flag is enabled
          hidden: !(
            isAccountActive && props.dataSecurityGeneralSettings.multiFactorAuthenticationEnabled
          ),
        },
      ]
        .filter(button => !button.hidden)
        .map(button => (
          <Button
            size="small"
            key={button.text}
            color={button.color || ""}
            materialIcon={button.materialIcon}
            onClick={button.onClick}
            className="w-182-px"
          >
            {button.text}
          </Button>
        ))}
    </View>
  );
  const changePasswordSchema = {
    type: "object",
    required: ["newPassword", "newPasswordConfirm"],
    properties: {
      newPassword: {
        type: "string",
        title: t("CHOOSE_NEW_PASSWORD"),
      },
      newPasswordConfirm: {
        type: "string",
        title: t("CONFIRM_NEW_PASSWORD"),
      },
    },
    urn: "urn:jsonschema:eu:firstcare:api:v1:dto:auth:ChangePasswordDTO",
  };

  const changePasswordUISchema = {
    newPassword: {
      "ui:widget": "password",
      "ui:options": {
        labelclass: "white-space-nowrap",
      },
    },
    newPasswordConfirm: {
      "ui:widget": "password",
      "ui:options": {
        labelclass: "white-space-nowrap",
        disabled: !changePasswordFormData || !changePasswordFormData.newPassword,
      },
    },
  };

  const columnsConfig = [
    {
      columns: [
        {
          columnTitle: props.t("ACCOUNT_DETAILS"),
          component: (
            <AccountDetails
              userAccount={props.userAccount}
              blockAccount={blockUserAccount}
              unblockAccount={unblockUserAccount}
              unlockTempBlockedAccount={handleUnlockUserAccount}
              onToggleSetEmailModal={handleToggleSetEmailModal}
            />
          ),
        },
        {
          component: accessLevelForm(),
        },
        {
          columnTitle: props.t(isAccountActive ? "PASSWORD" : ""),
          component: (function() {
            return (
              <View alignItems="end" gap={3} padding="1rem 0 0 0">
                {[
                  {
                    text: props.t("RESET"),
                    materialIcon: "lock_reset",
                    onClick: showResetPasswordModal,
                    hidden: !isAccountActive,
                  },
                  {
                    text: props.t("IMPERSONATE"),
                    color: "black",
                    materialIcon: "social_distance",
                    onClick: handleShowImpersonateModal,
                    hidden:
                      !props.hasPermission(PermissionEnum.PRIVILEGE_EMPLOYEE_IMPERSONATE) ||
                      !isAccountActive,
                  },
                  {
                    text: props.t("CHANGE_PASSWORD"),
                    color: "black",
                    materialIcon: "password",
                    onClick: toggleChangePasswordModal,
                    hidden: !showResetPasswordBtn,
                  },
                  {
                    text: props.t("RESET_OTP"),
                    color: "black",
                    materialIcon: "restart_alt",
                    onClick: handleResetOtp,
                    // show the reset otp button when account is active and multifactorial authentication flag is enabled
                    hidden: !(
                      isAccountActive &&
                      props.dataSecurityGeneralSettings.multiFactorAuthenticationEnabled
                    ),
                  },
                ]
                  .filter(button => !button.hidden)
                  .map(button => (
                    <Button
                      size="small"
                      key={button.text}
                      color={button.color || ""}
                      materialIcon={button.materialIcon}
                      onClick={button.onClick}
                      className="w-182-px"
                    >
                      {button.text}
                    </Button>
                  ))}
              </View>
            );
          })(),
        },
      ],
    },
  ];

  const formRef = useRef(null);

  useEffect(() => {
    const meetAllCriteria = Object.keys(passwordCriteria).every(k => {
      const isVal = passwordCriteria[k].validate(changePasswordFormData);
      return isVal;
    });

    setIsValidPassowrd(meetAllCriteria);
  }, [changePasswordFormData]);
  useEffect(() => {
    props.getPasswordPolicy({ clientId: props.clientId });
    props.getUserAccount({ clientId: props.clientId, userId: props.employeeId });
    props.getAccessLevelSchema({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
    props.getDataSecurityGeneralSettings({ clientId: props.clientId });
  }, []);

  function generateCriteria() {
    return {
      minNCharacters: {
        label: t("MINIMUM_N_CHARACTERS", {
          length: passwordPolicy["length"],
        }),
        validate: data => {
          if (!data) return false;
          return validate.minNCharacters(data.newPassword, passwordPolicy["length"]);
        },
      },
      lowerCase: {
        label: t("INCLUDE_LOWERCASE_LETTER", {
          lowerCase: passwordPolicy["lowerCase"],
        }),
        validate: data => {
          if (!data) return false;
          return validate.lowerCase(data.newPassword, passwordPolicy["lowerCase"]);
        },
      },
      upperCase: {
        label: t("INCLUDE_UPPERCASE_LETTER", {
          upperCase: passwordPolicy["upperCase"],
        }),
        validate: data => {
          if (!data) return false;
          return validate.upperCase(data.newPassword, passwordPolicy["upperCase"]);
        },
      },
      gsDigitsOrSpecialChars: {
        label: t("INCLUDE_NUMBER_OR_SPECIAL_CHARACTER", {
          gsDigitsOrSpecialChars: passwordPolicy["gsDigitsOrSpecialChars"],
        }),
        validate: data => {
          if (!data) return false;
          return validate.gsDigitsOrSpecialChars(
            data.newPassword,
            passwordPolicy["gsDigitsOrSpecialChars"],
          );
        },
      },
      match: {
        label: t("PASSWORD_CONFIRMATION_MATCHES"),
        validate: data => {
          if (!data) return false;
          return data.newPassword === data.newPasswordConfirm;
        },
      },
    };
  }

  function updateUserAccount(data: Object) {
    props.updateUserAccount({
      clientId: props.clientId,
      userId: props.employeeId,
      data,
    });
  }

  function updateAccessLevel(data: Object) {
    props.updateAccessLevel({
      clientId: props.clientId,
      employeeId: props.employeeId,
      data,
    });
  }

  function showAccountDetailsModal() {
    props.toggleAccountDetailsModal(true);
  }

  function closeAccountDetailsModal() {
    props.toggleAccountDetailsModal(false);
  }

  function submitAccountDetailsModal(enabled: boolean) {
    if (enabled) {
      unblockUserAccount();
    } else {
      props.blockUserAccount({
        clientId: props.clientId,
        userId: props.employeeId,
      });
    }

    closeAccountDetailsModal();
  }

  function blockUserAccount() {
    props.blockUserAccount({
      clientId: props.clientId,
      userId: props.employeeId,
    });
  }

  function unblockUserAccount() {
    props.unblockUserAccount({
      clientId: props.clientId,
      userId: props.employeeId,
    });
  }

  function showResetPasswordModal() {
    props.toggleConfirmResetPasswordModal(true);
  }

  function closeResetPasswordModal() {
    props.toggleConfirmResetPasswordModal(false);
  }

  function handleResetPassword() {
    props.resetUserAccountPassword({
      clientId: props.clientId,
      userId: props.employeeId,
    });
    closeResetPasswordModal();
  }

  function handleImpersonate() {
    props.impersonate({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
    handleCloseImpersonateModal();
  }

  function handleShowImpersonateModal() {
    props.toggleConfirmImpersonateModal({
      visible: true,
      employeeName: formatAuthoredBy(props.employee),
    });
  }
  function toggleChangePasswordModal() {
    setChangePasswordFormData(null);
    props.toggleChangePasswordModal(!props.isChangePasswordModalVisible);
  }
  function handleToggleSetEmailModal() {
    isEmailModalOpen(isEmailModalOpen);
  }

  function handleCloseImpersonateModal() {
    props.toggleConfirmImpersonateModal({
      visible: false,
    });
  }

  function handleUnlockUserAccount() {
    props.unlockUserAccount({
      clientId: props.clientId,
      userId: props.employeeId,
    });
  }

  function accessLevelForm() {
    if (!props.accessLevelSchema) {
      return null;
    }

    const leaveDate =
      props.employee && props.employee.leaveDate
        ? moment(props.employee.leaveDate).format("YYYY-MM-DD")
        : null;

    const tomorrow = moment()
      .add(1, "days")
      .format("YYYY-MM-DD");

    // when leave date is before today or is same with today, we define user as leaver
    const isLeaver = leaveDate ? moment(leaveDate).isBefore(tomorrow) : false;

    return (
      <EditAccessLevel
        data={props.accessLeveValue}
        schema={props.accessLevelSchema}
        onSubmit={updateAccessLevel}
        isLeaver={isLeaver}
      />
    );
  }
  function changePassword(changePasswordFormData: Object) {}

  function handleResetOtp() {
    props.resetOtp({ employeeId: props.employeeId });
  }

  function handleOnhangePassowrd(val) {
    let res = { ...changePasswordFormData, ...val };
    if (Object.keys(val).every(key => val && val[key] == null)) {
      res = null;
    }
    setChangePasswordFormData(res);
  }
  function handleConfirmChangePassowrd(val) {
    props.changePassword({ ...changePasswordFormData, userId: props.employeeId });
  }

  return (
    <SectionComponent wrapperClassName="flex-column pb-3">
      {props.isLoading && <Loader />}

      <ColumnsContainer rows={columnsConfig} className="pb-3" />
      {props.isEditAccountDetailsModalVisible && (
        <EditAccountDetailslModal
          enabled={isAccountActive}
          onSubmit={submitAccountDetailsModal}
          onDismissHandler={closeAccountDetailsModal}
        />
      )}
      {props.isConfirmResetPasswordModalVisible && (
        <ConfirmResetPasswordModal
          onSubmit={handleResetPassword}
          onDismissHandler={closeResetPasswordModal}
        />
      )}
      {props.impersonateModal.visible && (
        <ConfirmImpersonateModal
          modal={props.impersonateModal}
          onDismissHandler={handleCloseImpersonateModal}
          onSubmit={handleImpersonate}
        />
      )}
      {props.isChangePasswordModalVisible && (
        <ConfirmationModal
          modalSize="medium"
          headerText={props.t("CHANGE_PASSWORD")}
          bodyContent={
            <View direction="row" gap="5">
              <View flex="1">
                <SchemaForm
                  ref={formRef}
                  formData={changePasswordFormData}
                  schema={changePasswordSchema}
                  uiSchema={changePasswordUISchema}
                  onChange={handleOnhangePassowrd}
                  externalSubmit
                />
              </View>

              <View>
                <View className="checklist">
                  {Object.keys(passwordCriteria).map(criteria => {
                    const isValid = !!(
                      changePasswordFormData &&
                      passwordCriteria[criteria].validate(changePasswordFormData)
                    );

                    return (
                      <Text
                        className="item"
                        color={isValid && "risk-low"}
                        key={passwordCriteria[criteria].label}
                        text={passwordCriteria[criteria].label}
                      />
                    );
                  })}
                </View>
              </View>
            </View>
          }
          confirmBtnDisabled={!isValidPassowrd}
          confirmBtnLabel={t("CHANGE_PASSWORD")}
          cancelBtnLabel={t("CANCEL")}
          onConfirm={handleConfirmChangePassowrd}
          onCancel={toggleChangePasswordModal}
          noOldPassword={true}
          onDismissHandler={toggleChangePasswordModal}
        />
      )}
      {isEmailModalOpen && (
        <SetMissingEmailModal
          clientId={props.clientId}
          employeeId={props.employeeId}
          onClose={handleToggleSetEmailModal}
        />
      )}
    </SectionComponent>
  );
}

const mapStateToProps = (state: Object): PropsFromState => {
  return {
    isEditAccountDetailsModalVisible: employeeSelectors.getIsEditAccountDetailsModalVisible(state),
    isConfirmResetPasswordModalVisible: employeeSelectors.getIsConfirmResetPasswordModalVisible(
      state,
    ),
    impersonateModal: employeeSelectors.getImpersonateModal(state),
    userAccount: employeeSelectors.getUserAccount(state),
    accessLevelSchema: employeeSelectors.getAccessLeveSchema(state),
    accessLeveValue: employeeSelectors.getAccessLevelValue(state),
    isLoading: isLoading(state),
    userType: loginSelectors.selectUserType(state),
    isEmployeeAccountLocked: employeeSelectors.getEmployeeAccountLockedStatus(state),
    isAccountActive: employeeSelectors.isAccountActive(state),
    isChangePasswordModalVisible: profileSelectors.getChangePasswordModalVisible(state),
    dataSecurityGeneralSettings: serviceSelectors.dataSecurityGeneralSettings(state),
    passwordPolicy: serviceSelectors.getPasswordPolicy(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleAccountDetailsModal: employeeActions.ToggleEditAccountDetailsModal,
  toggleConfirmResetPasswordModal: employeeActions.ToggleConfirmResetPasswordModal,
  toggleConfirmImpersonateModal: employeeActions.ToggleUserAccountImpersonateModal,
  blockUserAccount: employeeActions.BlockUserAccount,
  unblockUserAccount: employeeActions.UnblockUserAccount,
  resetUserAccountPassword: employeeActions.ResetUserAccountPassword,
  getUserAccount: employeeActions.GetUserAccount,
  updateUserAccount: employeeActions.UpdateUserAccount,
  getAccessLevelSchema: employeeActions.GetAccessLevelSchema,
  updateAccessLevel: employeeActions.updateAccessLevel,
  impersonate: accountActionCreators.Impersonate,
  changePassword: profileActionCreators.ChangePassword,
  toggleChangePasswordModal: profileActionCreators.ToggleChangePasswordModal,
  resetOtp: employeeActions.ResetUserOtp,
  getDataSecurityGeneralSettings: serviceActions.GetDataSecurityGeneralSettings,
  unlockUserAccount: employeeActions.UnlockUserAccount,
  getPasswordPolicy: serviceActions.GetPasswordPolicy,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({}),
)(UserAccountDetailsGeneralInfo);
